import { burstMainScenario } from './burst-main';
import { streetWorksScenario } from './street-works';
import { connectedExperiencesScenario } from './connected-experiences';
import { capitalInvestmentScenario } from './capital-investment';
import { streetWorksPermitsScenario } from './street-works-permits';
import { mainsRehabProgrammeScenario } from './mains-rehab-programme';
import { energyTransitionScenario } from './energy-transition';
import { combinedSolutionScenario } from './combined-solution';

import type { ScenarioDefinition } from '../../core/types';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO REGISTRY
//
// Each scenario maps to one front-door pillar so the launcher can group the
// catalogue the way the sales narrative does. `definition` is the playable
// timeline; the surrounding fields are presentation-only metadata.
//
// To add a scenario: author `definitions/<id>.ts` with the authoring kit,
// then add one entry here. Nothing else in the engine needs to change.
// ──────────────────────────────────────────────────────────────────────────

export type PillarId =
  | 'combined-solution'
  | 'street-works'
  | 'connected-experiences'
  | 'intelligent-networks'
  | 'mains-rehab'
  | 'capital-investment'
  | 'energy-transition'
;

export const PILLAR_LABELS: Record<PillarId, string> = {
  'combined-solution': 'One Operating Layer — Combined Solution',
  'street-works': 'Frontier Mains Rehab & Planned Works Performance',
  'connected-experiences': 'Multi-channel Connected Experiences',
  'intelligent-networks': 'Intelligent Water & Wastewater Networks',
  'mains-rehab': 'Customer-centered & Sustainable Streetworks',
  'capital-investment': 'Visible & Intelligent Capital Investment Controls',
  'energy-transition': 'Sustainable Energy Transition',
};

export interface ScenarioMeta {
  id: string;
  title: string;
  pillar: PillarId;
  productTags: string[];
  durationSec: number;
  accent: string;
  summary: string;
  definition: ScenarioDefinition;
}

export const SCENARIO_REGISTRY: ScenarioMeta[] = [
  {
    id: combinedSolutionScenario.id,
    title: combinedSolutionScenario.title,
    pillar: 'combined-solution',
    productTags: ['Capital Delivery', 'ORP', 'Control Room', 'Opus'],
    durationSec: 230,
    accent: '#2dd4bf',
    summary: combinedSolutionScenario.description,
    definition: combinedSolutionScenario,
  },
  {
    id: burstMainScenario.id,
    title: burstMainScenario.title,
    pillar: 'intelligent-networks',
    productTags: ['Control Room', 'Novus', 'Tankering'],
    durationSec: 140,
    accent: '#ef4444',
    summary: burstMainScenario.description,
    definition: burstMainScenario,
  },
  {
    id: streetWorksScenario.id,
    title: streetWorksScenario.title,
    pillar: 'street-works',
    productTags: ['OPUS', 'Skewb Works', 'Core'],
    durationSec: 160,
    accent: '#f59e0b',
    summary: streetWorksScenario.description,
    definition: streetWorksScenario,
  },
  {
    id: connectedExperiencesScenario.id,
    title: connectedExperiencesScenario.title,
    pillar: 'connected-experiences',
    productTags: ['ConnectMe', 'Control Room'],
    durationSec: 165,
    accent: '#0ea5e9',
    summary: connectedExperiencesScenario.description,
    definition: connectedExperiencesScenario,
  },
  {
    id: capitalInvestmentScenario.id,
    title: capitalInvestmentScenario.title,
    pillar: 'capital-investment',
    productTags: ['Capital Delivery', 'Control Room'],
    durationSec: 165,
    accent: '#1e40af',
    summary: capitalInvestmentScenario.description,
    definition: capitalInvestmentScenario,
  },
  {
    id: streetWorksPermitsScenario.id,
    title: streetWorksPermitsScenario.title,
    pillar: 'mains-rehab',
    productTags: ['Skewb Works', 'OPUS', 'ConnectMe', 'Core'],
    durationSec: 175,
    accent: '#f97316',
    summary: streetWorksPermitsScenario.description,
    definition: streetWorksPermitsScenario,
  },
  {
    id: mainsRehabProgrammeScenario.id,
    title: mainsRehabProgrammeScenario.title,
    pillar: 'mains-rehab',
    productTags: ['Skewb Works', 'OPUS', 'Core'],
    durationSec: 170,
    accent: '#16a34a',
    summary: mainsRehabProgrammeScenario.description,
    definition: mainsRehabProgrammeScenario,
  },
  {
    id: energyTransitionScenario.id,
    title: energyTransitionScenario.title,
    pillar: 'energy-transition',
    productTags: ['Core', 'Tankering'],
    durationSec: 140,
    accent: '#059669',
    summary: energyTransitionScenario.description,
    definition: energyTransitionScenario,
  },
];

export const allScenarios: ScenarioDefinition[] = SCENARIO_REGISTRY.map((s) => s.definition);

export function getScenarioById(id: string): ScenarioDefinition | undefined {
  return SCENARIO_REGISTRY.find((s) => s.id === id)?.definition;
}

/** Registry grouped by pillar, in the canonical pillar order. */
export function scenariosByPillar(): Array<{ pillar: PillarId; label: string; items: ScenarioMeta[] }> {
  const order: PillarId[] = [
    'combined-solution',
    'intelligent-networks',
    'street-works',
    'connected-experiences',
    'mains-rehab',
    'capital-investment',
    'energy-transition',
  ];
  return order
    .map((pillar) => ({
      pillar,
      label: PILLAR_LABELS[pillar],
      items: SCENARIO_REGISTRY.filter((s) => s.pillar === pillar),
    }))
    .filter((g) => g.items.length > 0);
}
